import axios from 'axios';
import { API_BASE_URL } from '../constants';

// Create axios instance with default config
const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000
});

// Token storage key
const TOKEN_KEY = 'billing_token';

// Get token from localStorage
export const getToken = () => localStorage.getItem(TOKEN_KEY);

// Set token in localStorage
export const setToken = (token) => {
  if (token) {
    localStorage.setItem(TOKEN_KEY, token);
  } else {
    localStorage.removeItem(TOKEN_KEY);
  }
};

// Remove token from localStorage
export const removeToken = () => localStorage.removeItem(TOKEN_KEY);

// Request interceptor - add auth token to all requests
api.interceptors.request.use(
  (config) => {
    const token = getToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor - handle 401/403 errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401 || error.response?.status === 403) {
      // Token expired or invalid
      removeToken();
      localStorage.removeItem('billing_user');
      // Redirect to login if not already there
      if (window.location.pathname !== '/') {
        window.location.reload();
      }
    }
    return Promise.reject(error);
  }
);

// Clients API
export const clientsAPI = {
  getAll: () => api.get('/clients'),
  create: (name) => api.post('/clients', { name }),
  delete: (name) => api.delete(`/clients/${encodeURIComponent(name)}`)
};

// Applications API
export const applicationsAPI = {
  getAll: () => api.get('/applications'),
  create: (name) => api.post('/applications', { name }),
  delete: (name) => api.delete(`/applications/${encodeURIComponent(name)}`)
};

// Service Templates API
export const templatesAPI = {
  getAll: () => api.get('/service-templates'),
  create: (template) => api.post('/service-templates', template),
  update: (id, template) => api.put(`/service-templates/${id}`, template),
  delete: (serviceName) => api.delete(`/service-templates/${encodeURIComponent(serviceName)}`)
};

// File Upload API
export const uploadAPI = {
  upload: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
  }
};

// Cloud Module API (rediseño 2026-05)
export const procesoAPI = {
  // Clientes
  getClientes: () => api.get('/proceso/clientes'),
  createCliente: (name) => api.post('/proceso/clientes', { name }),
  deleteCliente: (id) => api.delete(`/proceso/clientes/${id}`),
  reordenarClientes: (orden) => api.put('/proceso/clientes/orden', { orden }),
  // Prorrateo (matriz servicio-instancia x cliente)
  getProrrateo: () => api.get('/proceso/prorrateo'),
  saveProrrateoCelda: (celda) => api.put('/proceso/prorrateo', celda),
  saveProrrateoFila: (fila) => api.put('/proceso/prorrateo/fila', fila)
};

// TBBC Module API (rediseño 2026-05)
export const tbbcAPI = {
  // Proyectos (reusa la tabla clients)
  getProyectos: () => api.get('/tbbc/proyectos'),
  createProyecto: (name) => api.post('/tbbc/proyectos', { name }),
  deleteProyecto: (id) => api.delete(`/tbbc/proyectos/${id}`),
  reordenarProyectos: (orden) => api.put('/tbbc/proyectos/orden', { orden }),
  // Catálogo de apps con costo
  getApps: () => api.get('/tbbc/apps'),
  createApp: (name, costo, modo) => api.post('/tbbc/apps', { name, costo, modo }),
  updateApp: (id, data) => api.put(`/tbbc/apps/${id}`, data),
  deleteApp: (id) => api.delete(`/tbbc/apps/${id}`),
  reordenarApps: (orden) => api.put('/tbbc/apps/orden', { orden }),
  // Asignaciones app <-> proyecto (la app carga su costo completo al proyecto)
  getAsignaciones: () => api.get('/tbbc/prorrateo'),
  setAsignacion: (asignacion) => api.put('/tbbc/prorrateo', asignacion)
};

// AXD Module API (clon de TBBC con su propio catálogo de herramientas)
export const axdAPI = {
  // Proyectos (reusa la tabla clients)
  getProyectos: () => api.get('/axd/proyectos'),
  createProyecto: (name) => api.post('/axd/proyectos', { name }),
  deleteProyecto: (id) => api.delete(`/axd/proyectos/${id}`),
  reordenarProyectos: (orden) => api.put('/axd/proyectos/orden', { orden }),
  // Catálogo de herramientas con costo
  getApps: () => api.get('/axd/apps'),
  createApp: (name, costo, modo) => api.post('/axd/apps', { name, costo, modo }),
  updateApp: (id, data) => api.put(`/axd/apps/${id}`, data),
  deleteApp: (id) => api.delete(`/axd/apps/${id}`),
  reordenarApps: (orden) => api.put('/axd/apps/orden', { orden }),
  // Asignaciones app <-> proyecto (la app carga su costo completo al proyecto)
  getAsignaciones: () => api.get('/axd/prorrateo'),
  setAsignacion: (asignacion) => api.put('/axd/prorrateo', asignacion)
};

// Optimize Module API (clon de AXD con su propio catálogo de herramientas)
export const optimizeAPI = {
  // Proyectos (reusa la tabla clients)
  getProyectos: () => api.get('/optimize/proyectos'),
  createProyecto: (name) => api.post('/optimize/proyectos', { name }),
  deleteProyecto: (id) => api.delete(`/optimize/proyectos/${id}`),
  reordenarProyectos: (orden) => api.put('/optimize/proyectos/orden', { orden }),
  // Catálogo de herramientas con costo
  getApps: () => api.get('/optimize/apps'),
  createApp: (name, costo, modo) => api.post('/optimize/apps', { name, costo, modo }),
  updateApp: (id, data) => api.put(`/optimize/apps/${id}`, data),
  deleteApp: (id) => api.delete(`/optimize/apps/${id}`),
  reordenarApps: (orden) => api.put('/optimize/apps/orden', { orden }),
  // Asignaciones app <-> proyecto (la app carga su costo completo al proyecto)
  getAsignaciones: () => api.get('/optimize/prorrateo'),
  setAsignacion: (asignacion) => api.put('/optimize/prorrateo', asignacion)
};

// Áreas API: clasifica las herramientas de TBBC / AXD / Optimize en
// Mesa de Servicio, Soporte en Sitio o ambas (area null = sin asignar).
// Con 'ambas', pctMesa es la parte del costo que va a Mesa; el resto, a Sitio.
export const areasAPI = {
  getHerramientas: () => api.get('/areas/herramientas'),
  setArea: (catalogo, id, area, pctMesa) =>
    api.put(`/areas/herramientas/${catalogo}/${id}`, { area, pctMesa })
};

// Costos Cloud: histórico del CSV de IBM Cloud por mes (periodo = 'AAAA-MM') con su TRM.
export const cortesAPI = {
  getAll: () => api.get('/cortes'),
  get: (periodo) => api.get(`/cortes/${periodo}`),
  // formData: file, periodo, trm (opcional), reemplazar ('true' para sobrescribir)
  cargar: (formData) => api.post('/cortes', formData, { timeout: 120000 }),
  setTrm: (id, trm) => api.put(`/cortes/${id}`, { trm }),
  eliminar: (id) => api.delete(`/cortes/${id}`)
};

// Distribución de servicios: reglas que asignan recursos de Cloud a aplicaciones.
// regla = { tipo: 'exacto'|'contiene', patron, servicio?, todas, apps: [{ key, pct? }] }
export const distribucionAPI = {
  getApps: () => api.get('/distribucion/apps'),
  getReglas: () => api.get('/distribucion/reglas'),
  getRecursos: (periodo) => api.get('/distribucion/recursos', { params: { periodo } }),
  crearRegla: (regla) => api.post('/distribucion/reglas', regla),
  // Una regla exacta por recurso hacia todas las aplicaciones: recursos = [instance]
  asignarLote: (recursos) => api.post('/distribucion/reglas/lote', { recursos }),
  editarRegla: (id, regla) => api.put(`/distribucion/reglas/${id}`, regla),
  eliminarRegla: (id) => api.delete(`/distribucion/reglas/${id}`)
};

// Liquidación: por mes, cuánto paga cada cliente (Cloud + proveedor) y su
// reparto entre Mesa de Servicio y Soporte en Sitio.
export const liquidacionAPI = {
  get: (periodo) => api.get('/liquidacion', { params: { periodo } })
};

// Costos Proveedor: tarifas por contrato (normalmente anual). Un ajuste de
// tarifa es la excepción y queda en el historial con su fecha y motivo.
export const contratosAPI = {
  getVigentes: (periodo) => api.get('/contratos/vigentes', { params: { periodo } }),
  crear: (contrato) => api.post('/contratos', contrato),
  editar: (id, datos) => api.put(`/contratos/${id}`, datos),
  eliminar: (id) => api.delete(`/contratos/${id}`),
  // { app_id, name, costo, desde: 'AAAA-MM', motivo }
  guardarTarifa: (id, tarifa) => api.post(`/contratos/${id}/tarifas`, tarifa),
  deshacerAjuste: (tarifaId) => api.delete(`/contratos/tarifas/${tarifaId}`),
  quitarHerramienta: (id, appId) => api.delete(`/contratos/${id}/herramientas/${appId}`)
};

// Reportes API (el backend arma el Excel al vuelo contra la base)
export const reportsAPI = {
  // Libro completo: resumen, proyectos, aplicaciones y las matrices de cada modulo
  getCostos: () => api.get('/reports/costos', { responseType: 'blob' }),
  // Version plana: Recurso | Aplicacion | Cliente | Costo Aprox
  getDetalle: () => api.get('/reports/detalle', { responseType: 'blob' })
};

// Auth API (uses plain axios, not the interceptor)
export const authAPI = {
  login: (username, password) => axios.post(`${API_BASE_URL}/login`, { username, password }),
  sendCode: (phone) => axios.post(`${API_BASE_URL}/auth/send-code`, { phone }),
  verifyCode: (phone, code) => axios.post(`${API_BASE_URL}/auth/verify-code`, { phone, code }),
  verifyToken: () => api.get('/verify-token')
};

// Compartir con gerencia: enlaces sin usuario para descargar el informe PDF.
export const gerenciaAPI = {
  getAccesos: () => api.get('/gerencia/accesos'),
  crear: (acceso) => api.post('/gerencia/accesos', acceso),
  editar: (id, datos) => api.put(`/gerencia/accesos/${id}`, datos),
  eliminar: (id) => api.delete(`/gerencia/accesos/${id}`),
  historial: (id) => api.get(`/gerencia/accesos/${id}/historial`)
};

// Página pública del gerente (axios simple: sin JWT ni redirección al login).
export const informePublicoAPI = {
  get: (token) => axios.get(`${API_BASE_URL}/public/informe/${encodeURIComponent(token)}`),
  liquidacion: (token, periodo, trm, formato = 'pdf') =>
    axios.get(`${API_BASE_URL}/public/informe/${encodeURIComponent(token)}/liquidacion`, { params: { periodo, trm, formato } })
};
